// Hulpfuncties voor de kennisbank (/kennisbank): thema's, zoeken, gerelateerde
// artikelen en de lichte WebP-versies van de hero-afbeeldingen.

// Vijf thema's in plaats van dertien losse categorieën. Een artikel valt via zijn
// categorie in een thema; een paar artikelen staan bij slug in een ander thema
// omdat hun categorie te algemeen is.
export const THEMAS = [
  { id: 'toetsen-maken', naam: 'Toetsen maken', kleur: '#7dd3fc' },
  { id: 'ai-en-data', naam: 'AI en data', kleur: '#c4b5fd' },
  { id: 'privacy', naam: 'Privacy en veiligheid', kleur: '#fca5a5' },
  { id: 'in-de-klas', naam: 'In de klas', kleur: '#86efac' },
  { id: 'starten', naam: 'Starten en kiezen', kleur: '#fcd34d' },
];

const THEMA_PER_CATEGORIE = {
  'Toetsdesign': 'toetsen-maken',
  'Onderwijs': 'toetsen-maken',
  'Formatief Toetsen': 'toetsen-maken',
  'Talen': 'toetsen-maken',
  'Wiskunde': 'toetsen-maken',
  'AI & Technologie': 'ai-en-data',
  'Technologie': 'ai-en-data',
  'Toetsanalyse': 'ai-en-data',
  'Privacy & Compliance': 'privacy',
  'Beveiliging': 'privacy',
  'Betrouwbaarheid': 'privacy',
  'Differentiatie': 'in-de-klas',
  'Leskwaliteit': 'in-de-klas',
  'Welzijn': 'in-de-klas',
  'Inclusiviteit': 'in-de-klas',
  'Examentraining': 'in-de-klas',
  'Handleiding': 'starten',
  'Vergelijking': 'starten',
};

const THEMA_PER_SLUG = {
  'toetssoftware-onderwijs-kiezen': 'starten',
};

export function themaVan(artikel) {
  const id = THEMA_PER_SLUG[artikel?.slug] || THEMA_PER_CATEGORIE[artikel?.category] || 'toetsen-maken';
  return THEMAS.find((t) => t.id === id);
}

// Nieuwste eerst; bij gelijke datum blijft de volgorde uit blogArticles staan.
export function nieuwsteEerst(artikelen) {
  return [...artikelen].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

// Hoofdletters en accenten maken niet uit: "differentieren" vindt "Differentiëren".
function normaliseer(tekst) {
  return (tekst || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

// Elk zoekwoord moet ergens in titel, samenvatting, categorie of thema staan.
export function filterArtikelen(artikelen, { zoek = '', thema = null } = {}) {
  const woorden = normaliseer(zoek).split(/\s+/).filter(Boolean);
  return artikelen.filter((a) => {
    const t = themaVan(a);
    if (thema && t.id !== thema) return false;
    if (woorden.length === 0) return true;
    const hooiberg = normaliseer([a.title, a.excerpt, a.category, t.naam].join(' '));
    return woorden.every((w) => hooiberg.includes(w));
  });
}

// Aantal artikelen per thema bij de huidige zoekterm, los van het gekozen thema.
// Zo klopt het getal op een themaknop met wat je krijgt als je erop klikt
// (19-10-2026: bij "avg" bleef "Toetsen maken 10" staan en gaf 0 artikelen).
// `alles` = het getal op de knop Alles.
export function telPerThema(artikelen, zoek = '') {
  const telling = { alles: 0 };
  filterArtikelen(artikelen, { zoek }).forEach((a) => {
    const id = themaVan(a).id;
    telling[id] = (telling[id] || 0) + 1;
    telling.alles += 1;
  });
  return telling;
}

// Drie gerelateerde artikelen: eerst hetzelfde thema, daarna de nieuwste andere.
// Voorheen toonde elk artikel alle 24 andere (24 extra afbeeldingen per pagina).
export function gerelateerd(artikelen, huidig, aantal = 3) {
  const anderen = nieuwsteEerst(artikelen.filter((a) => a.slug !== huidig.slug));
  const thema = themaVan(huidig).id;
  const zelfdeThema = anderen.filter((a) => themaVan(a).id === thema);
  const rest = anderen.filter((a) => themaVan(a).id !== thema);
  return [...zelfdeThema, ...rest].slice(0, aantal);
}

// De PNG blijft bestaan voor og:image (social media); op de site zelf tonen we
// WebP: groot (1200 px) op de artikelpagina, klein (720 px) op de kaarten.
export function grootBeeld(pad) {
  return pad && pad.endsWith('.png') ? pad.replace(/\.png$/, '.webp') : pad;
}

export function kaartBeeld(pad) {
  return pad && pad.endsWith('.png') ? pad.replace(/\.png$/, '-kaart.webp') : pad;
}

// Ankers voor de inhoudsopgave: "Wat is een toetsmatrijs?" → "wat-is-een-toetsmatrijs".
export function ankerVan(tekst) {
  return normaliseer(tekst)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Een tekst in zinnen. Een zin eindigt op . ? of ! gevolgd door witruimte of het
// einde: "WCAG 2.2" en "5,5" breken dus niet. Het leesteken blijft bij de zin.
export function zinnenVan(tekst) {
  const t = (tekst || '').trim();
  const zinnen = [];
  let begin = 0;
  for (let i = 0; i < t.length; i += 1) {
    if ('.?!'.includes(t[i]) && (i + 1 === t.length || /\s/.test(t[i + 1]))) {
      zinnen.push(t.slice(begin, i + 1).trim());
      begin = i + 1;
    }
  }
  if (t.slice(begin).trim()) zinnen.push(t.slice(begin).trim());
  return zinnen;
}

// De eerste zin, mét leesteken (llms.txt): "Bij hoeveel punten staat een 5,5?".
export function eersteZin(tekst) {
  return zinnenVan(tekst)[0] || '';
}

// De regels onder "## Kennisbank" in public/llms.txt: nieuwste eerst, met de
// eerste zin van de samenvatting. Zo bleef bij een vraag het vraagteken weg
// ("Bij hoeveel punten staat een 5,5"), 19-10-2026. Opnieuw maken:
// node scripts/kennisbank-llms.mjs
export function llmsKennisbankRegels(artikelen) {
  return nieuwsteEerst(artikelen).map(
    (a) => `- [${a.title}](https://testwisely.nl/kennisbank/${a.slug}): ${eersteZin(a.excerpt)}`,
  );
}

// Meta description: zoveel hele zinnen als passen binnen `max` tekens, zodat
// Google hem niet midden in een zin afkapt (19-10-2026: 59 van de 77 pagina's
// waren langer dan 160). Past de eerste zin al niet, dan op een woordgrens met "…".
export function heleZinnen(tekst, max = 160) {
  const t = (tekst || '').trim();
  if (t.length <= max) return t;
  let uit = '';
  for (const zin of zinnenVan(t)) {
    const erbij = uit ? `${uit} ${zin}` : zin;
    if (erbij.length > max) break;
    uit = erbij;
  }
  if (uit) return uit;
  const kort = t.slice(0, max - 1);
  const spatie = kort.lastIndexOf(' ');
  return `${(spatie > 0 ? kort.slice(0, spatie) : kort).replace(/[\s,;:]+$/, '')}…`;
}

// Koppen die zelf al een nummer hebben ("1. Verwerkersovereenkomst"). Dan
// nummert de inhoudsopgave niet nog een keer: dat gaf "3. 1. Verwerkersovereenkomst".
export function heeftEigenNummers(koppen) {
  return koppen.some((k) => /^\d+[.)]\s/.test(k.text || ''));
}

// Waar het blok "Lees ook" komt: aan het eind van de sectie die het dichtst bij
// het midden ligt, dus vlak vóór een tussenkop. Voorheen kwam het na blok
// length/2, en dat was in 13 artikelen een kop: kop, Lees ook, dan pas de tekst.
// Geeft de index van het blok waarna "Lees ook" komt.
export function leesOokPlek(content) {
  const midden = Math.floor(content.length / 2);
  let beste = -1;
  for (let i = 1; i < content.length; i += 1) {
    const ervoor = content[i - 1].type;
    if (content[i].type !== 'heading' || ervoor === 'heading' || ervoor === 'links') continue;
    if (beste === -1 || Math.abs(i - 1 - midden) < Math.abs(beste - midden)) beste = i - 1;
  }
  if (beste !== -1) return beste;
  // Geen tussenkoppen: na het middelste blok, maar nooit direct na een kop.
  return content[midden]?.type === 'heading' ? Math.max(0, midden - 1) : midden;
}
